import {Button} from '@dabafinance/react-components';
import {Input, Select, Table, Tag} from 'antd';
import {useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import {GET_NOTIFICATION_BUCKETS} from 'graphql/queries/notificationOrchestrator';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import BucketForm from './BucketForm';
import styles from './workflowOrchestrator.module.scss';

const DROP_POLICY_LABEL = {
  SHIFT_INDEFINITELY: 'Shift forever',
  DROP_AFTER_MAX_SHIFT: 'Drop after max shift',
};

const Buckets = () => {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState(undefined);
  const [modal, setModal] = useState({open: false, bucket: null});

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.NotificationOrchestrator),
  );

  const {data, loading, refetch} = useQuery(GET_NOTIFICATION_BUCKETS, {
    variables: {input: {}},
    fetchPolicy: 'no-cache',
    onError: error => toast.error(error.message),
  });

  const buckets = data?.getNotificationBuckets?.data || [];

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return buckets.filter(b => {
      if (activeFilter === 'ACTIVE' && !b.active) return false;
      if (activeFilter === 'INACTIVE' && b.active) return false;
      if (!term) return true;
      return (
        b.key?.toLowerCase().includes(term) ||
        b.name?.toLowerCase().includes(term) ||
        b.description?.toLowerCase().includes(term)
      );
    });
  }, [buckets, search, activeFilter]);

  const columns = [
    {title: 'Key', dataIndex: 'key', key: 'key'},
    {title: 'Name', dataIndex: 'name', key: 'name'},
    {
      title: 'Cooldown',
      dataIndex: 'cooldownHours',
      key: 'cooldownHours',
      render: hours => (hours != null ? `${hours} h` : '—'),
    },
    {
      title: 'Drop policy',
      dataIndex: 'dropPolicy',
      key: 'dropPolicy',
      render: (_, record) => (
        <div>
          <div>{DROP_POLICY_LABEL[record.dropPolicy] || record.dropPolicy}</div>
          {record.dropPolicy === 'DROP_AFTER_MAX_SHIFT' &&
            record.dropAfterShiftHours != null && (
              <small style={{color: '#8b82a7'}}>
                Max delay {record.dropAfterShiftHours} h
              </small>
            )}
        </div>
      ),
    },
    {
      title: 'Approval',
      dataIndex: 'requiresApproval',
      key: 'requiresApproval',
      render: required => (
        <Tag color={required ? 'orange' : 'default'}>
          {required ? 'Required' : '—'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: active => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'operation',
      key: 'actions',
      render: (_, record) => (
        <Button
          label="Edit"
          type="secondary"
          disabled={!tabOperations?.update}
          onClick={() => setModal({open: true, bucket: record})}
        />
      ),
    },
  ];

  return (
    <div>
      <div className={styles.filters}>
        <Input
          allowClear
          placeholder="Search by key, name or description"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{width: 320}}
        />
        <Select
          allowClear
          placeholder="Status"
          value={activeFilter}
          onChange={setActiveFilter}
          style={{width: 160}}>
          <Select.Option value="ACTIVE">Active</Select.Option>
          <Select.Option value="INACTIVE">Inactive</Select.Option>
        </Select>
        <Button
          label="New Bucket"
          className={styles['button-style']}
          disabled={!tabOperations?.create}
          onClick={() => setModal({open: true, bucket: null})}
        />
      </div>

      <div className={styles.body}>
        <Table
          rowKey={record => record.id || record.key}
          loading={loading}
          dataSource={filtered}
          columns={columns}
          pagination={{pageSize: 10, showSizeChanger: false}}
        />
      </div>

      <DBModal
        isOpen={modal.open}
        handleClose={() => setModal({open: false, bucket: null})}
        width={680}
        content={
          <BucketForm
            bucket={modal.bucket}
            onClose={() => setModal({open: false, bucket: null})}
            onSaved={() => refetch()}
          />
        }
      />
    </div>
  );
};

export default Buckets;
